import type { RouteRecordRaw } from "vue-router";
import FeatureFlags from "@/enum/FeatureFlags";
import settingsRoutes from "@/router/modules/settingsRoutes";
import profileMailRoutes from "@/router/modules/profileMailRoutes";
import { legacyRedirectRoutes } from "@/views/settings/lib/redirects";

const Layout = () => import("@/layout/index.vue");

const asyncRoutes: RouteRecordRaw[] = [
  ...settingsRoutes,
  ...profileMailRoutes,
  ...legacyRedirectRoutes,
  {
    path: "/assistant",
    component: Layout,
    name: "Assistant",
    meta: {
      requiresAuth: true,
      roles: ["user"],
      hidden: true,
      cff: [FeatureFlags.AI_VOICE_ASSISTANT],
    },
    children: [
      {
        path: "",
        name: "AssistantVoice",
        component: () => import("@/views/assistant/index.vue"),
        meta: {
          noCache: true,
          title: "assistant",
          hidden: true,
          cff: [FeatureFlags.AI_VOICE_ASSISTANT],
        },
      },
    ],
  },
  {
    path: "/contacts",
    component: Layout,
    name: "Contacts",
    meta: {
      requiresAuth: true,
      roles: ["user"],
      cff: [FeatureFlags.CONTACTS_APP],
    },
    children: [
      {
        path: "",
        name: "ContactsList",
        component: () => import("@/views/contacts/list/index.vue"),
        meta: {
          title: "contacts",
          icon: "address-book",
          noCache: true,
        },
      },
      {
        path: ":id",
        name: "ContactView",
        component: () => import("@/views/contacts/view/index.vue"),
        meta: {
          title: "contacts",
          hidden: true,
        },
      },
      {
        // Review of merging contact :id (source, deleted afterwards) into :targetId.
        path: ":id/merge/:targetId",
        name: "ContactMerge",
        component: () => import("@/views/contacts/merge/ContactMerge.vue"),
        meta: {
          title: "contacts",
          hidden: true,
          roles: ["manager", "tech_admin"],
        },
      },
    ],
  },
  {
    path: "/tasks",
    component: Layout,
    name: "Tasks",
    meta: {
      requiresAuth: true,
      roles: ["user", "contact"],
      title: "tasks",
    },
    children: [
      {
        path: "",
        name: "TasksList",
        component: () => import("@/views/tasks/list/index.vue"),
        meta: {
          icon: "tasks",
          noCache: true,
          cff: [FeatureFlags.TASKS_APP],
        },
      },
      {
        path: "new",
        name: "TaskNew",
        component: () => import("@/views/tasks/new/index.vue"),
        meta: {
          noCache: true,
          title: "new",
          hidden: true,
          cff: [FeatureFlags.TASKS_APP],
        },
      },
      {
        path: "recurring",
        name: "RecurringTasksList",
        component: () => import("@/views/tasks/recurring/list/index.vue"),
        meta: {
          noCache: true,
          title: "recurring_tasks",
          hidden: true,
          roles: ["user"],
          cff: [FeatureFlags.RECURRING_TASKS],
        },
      },
      {
        path: "recurring/new",
        name: "RecurringTaskNew",
        component: () => import("@/views/tasks/recurring/edit/index.vue"),
        meta: {
          noCache: true,
          title: "recurring_tasks",
          hidden: true,
          roles: ["user"],
          cff: [FeatureFlags.RECURRING_TASKS],
        },
      },
      {
        path: "recurring/:id",
        name: "RecurringTaskEdit",
        component: () => import("@/views/tasks/recurring/edit/index.vue"),
        meta: {
          noCache: true,
          title: "recurring_tasks",
          hidden: true,
          roles: ["user"],
          cff: [FeatureFlags.RECURRING_TASKS],
        },
      },
      {
        path: ":id",
        name: "TaskView",
        component: () => import("@/views/tasks/view/index.vue"),
        meta: {
          noCache: true,
          title: "",
          hidden: true,
        },
      },
    ],
  },
  {
    path: "/my-week",
    component: Layout,
    name: "MyWeekRoot",
    meta: {
      requiresAuth: true,
      roles: ["user"],
      cff: [FeatureFlags.MY_WEEK],
    },
    children: [
      {
        path: "",
        name: "MyWeek",
        component: () => import("@/views/my-week/index.vue"),
        meta: {
          title: "my_week",
          icon: "calendar-check",
          noCache: true,
        },
      },
    ],
  },
  {
    path: "/protocols",
    component: Layout,
    name: "Protocols",
    meta: {
      requiresAuth: true,
      roles: ["user", "contact"],
      title: "protocols",
    },
    children: [
      {
        path: "",
        name: "ProtocolsList",
        component: () => import("@/views/protocols/list/index.vue"),
        meta: {
          icon: "clipboard-list",
          noCache: true,
          cff: [FeatureFlags.PROTOCOLS_APP],
        },
      },
      {
        path: "new",
        name: "ProtocolNew",
        component: () => import("@/views/protocols/new/index.vue"),
        meta: {
          noCache: true,
          title: "New",
          hidden: true,
          cff: [FeatureFlags.PROTOCOLS_APP],
        },
      },
      {
        path: ":id",
        name: "ProtocolView",
        component: () => import("@/views/protocols/view/index.vue"),
        meta: {
          title: "",
          hidden: true,
        },
      },
    ],
  },
  {
    path: "/mails",
    component: Layout,
    name: "Mails",
    meta: {
      roles: ["user"],
      cff: [FeatureFlags.MAIL_APP],
    },
    children: [
      {
        path: "",
        name: "MailsList",
        component: () => import("@/views/mails/list/index.vue"),
        meta: {
          title: "mails",
          icon: "envelope",
          sidebarLeaf: true,
        },
      },
    ],
  },
  {
    path: "/correspondence",
    component: Layout,
    name: "Correspondence",
    meta: {
      roles: ["contact"],
      cff: [FeatureFlags.MAIL_APP],
    },
    children: [
      {
        path: "",
        name: "CorrespondenceList",
        component: () => import("@/views/mails/list/index.vue"),
        props: { portal: true },
        meta: {
          title: "correspondence",
          icon: "envelope",
          sidebarLeaf: true,
        },
      },
    ],
  },
  {
    path: "/admin",
    component: Layout,
    redirect: "/settings",
    name: "Admin",
    meta: {
      title: "admin",
      requiresAuth: true,
      icon: "el-icon-s-operation",
      roles: ["admin"],
      hidden: true,
    },
    children: [
      {
        path: "accounts",
        name: "AdminAccounts",
        component: () => import("@/views/admin/list/index.vue"),
        meta: {
          title: "admin",
          hidden: true,
        },
      },
      {
        path: "accounts/:id",
        name: "AdminAccountView",
        component: () => import("@/views/admin/view/index.vue"),
        meta: {
          title: "admin",
          hidden: true,
        },
      },
      {
        // One space of a customer account, managed by a super admin (#290).
        path: "accounts/:account_id/spaces/:id/:tab?",
        name: "AdminAccountSpace",
        component: () => import("@/views/admin/spaces/AdminSpacePage.vue"),
        meta: {
          title: "admin",
          hidden: true,
          // Switching tabs changes the path; the page stays mounted.
          shellKey: "AdminAccountSpace",
        },
      },
    ],
  },
  // 404 page must be placed at the end
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
    name: "NotFoundCatchAll",
    meta: {
      hidden: true,
    },
  },
];

export default asyncRoutes;
